import React, { useEffect, useState } from "react";
import SummaryApi from "../common";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";

export default function UserProfile() {
  const [user, setUser] = useState(null);
  const navigate = useNavigate();
  const [editForm, setEditForm] = useState({
    name: "",
    email: "",
    contact: "",
    totalSeats: 0,
  });

  // Fetch user profile
  const fetchProfile = async () => {
    try {
      const res = await fetch(SummaryApi.getUserProfile.url, {
        method: SummaryApi.getUserProfile.method,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("authToken")}`,
        },
      });

      const data = await res.json();
      if (data.success) {
        setUser(data.user);
        setEditForm({
          name: data.user.name,
          email: data.user.email,
          contact: data.user.contact,
          totalSeats: data.user.totalSeats,
        });
      } else {
        toast.error("Failed to load profile");
      }
    } catch (err) {
      console.log(err);
      toast.error("Error loading profile");
    }
  };

  useEffect(() => {
    fetchProfile();
  }, []);

  const handleSave = async () => {
    try {
      const res = await fetch(SummaryApi.updateUserProfile.url, {
        method: SummaryApi.updateUserProfile.method,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("authToken")}`,
        },
        body: JSON.stringify(editForm),
      });

      const data = await res.json();
      if (data.success) {
        toast.success("Profile updated!");
        fetchProfile();
      } else {
        toast.error("Failed to update profile");
      }
    } catch (err) {
      toast.error("Error saving profile");
    }
  };

  if (!user)
    return (
      <div className="flex items-center justify-center min-h-screen">
        <p className="text-lg font-semibold">Loading Profile...</p>
      </div>
    );

  // Auto-calculation
  const assignedSeats = user.assignedSeats;
  const vacantSeats = editForm.totalSeats - assignedSeats;

  return (
    <div className="max-w-4xl mx-auto p-6 mt-10">
    <button
        onClick={() => navigate("/library-dashboard")}
        className="mb-4 flex items-center gap-2 text-gray-600 hover:text-gray-800 transition-colors group"
      >
        <svg
          className="w-5 h-5 group-hover:-translate-x-1 transition-transform"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M10 19l-7-7m0 0l7-7m-7 7h18"
          />
        </svg>
        <span className="font-medium">Back to Dashboard</span>
      </button>
      {/* Header */}
      <h1 className="text-3xl font-bold text-gray-800 mb-6">Edit Profile</h1>

      <div className="bg-white rounded-xl shadow p-6 space-y-6">

        {/* Basic Info - Editable */}
        <div>
          <h2 className="text-xl font-semibold text-gray-700 mb-3">Personal Details</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            
            {/* Name */}
            <div>
              <label className="block text-xs text-gray-500 uppercase mb-1">Name</label>
              <input
                className="border p-3 w-full rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                value={editForm.name}
                onChange={(e) =>
                  setEditForm({ ...editForm, name: e.target.value })
                }
              />
            </div>

            {/* Email */}
            <div>
              <label className="block text-xs text-gray-500 uppercase mb-1">Email</label>
              <input
                type="email"
                className="border p-3 w-full rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                value={editForm.email}
                onChange={(e) =>
                  setEditForm({ ...editForm, email: e.target.value })
                }
              />
            </div>

            {/* Contact */}
            <div>
              <label className="block text-xs text-gray-500 uppercase mb-1">Contact</label>
              <input
                className="border p-3 w-full rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                value={editForm.contact}
                onChange={(e) =>
                  setEditForm({ ...editForm, contact: e.target.value })
                }
              />
            </div>

            {/* Role (Read-only) */}
            <div>
              <label className="block text-xs text-gray-500 uppercase mb-1">Role</label>
              <input
                disabled
                className="border p-3 w-full rounded-lg bg-gray-50"
                value={user.role}
              />
            </div>

            {/* Plan (Read-only) */}
            <div>
              <label className="block text-xs text-gray-500 uppercase mb-1">Plan</label>
              <input
                disabled
                className="border p-3 w-full rounded-lg bg-gray-50"
                value={user.plan}
              />
            </div>

            {/* Status (Read-only) */}
            <div>
              <label className="block text-xs text-gray-500 uppercase mb-1">Status</label>
              <input
                disabled
                className="border p-3 w-full rounded-lg bg-gray-50"
                value={user.status}
              />
            </div>

          </div>
        </div>

        {/* Seat Management Section */}
        <div>
          <h2 className="text-xl font-semibold text-gray-700 mb-3">Seat Management</h2>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">

            {/* Total Seats (Editable) */}
            <div className="p-4 bg-blue-50 border-l-4 border-blue-500 rounded-lg">
              <label className="block text-sm text-gray-600 mb-2">Total Seats</label>
              <input
              min={0}
                type="number"
                className="w-full text-3xl font-bold text-blue-600 bg-transparent border-none focus:outline-none"
                value={editForm.totalSeats}
                onChange={(e) =>
                  setEditForm({
                    ...editForm,
                    totalSeats: Number(e.target.value),
                  })
                }
              />
            </div>

            {/* Assigned Seats (Auto) */}
            <div className="p-4 bg-green-50 border-l-4 border-green-500 rounded-lg">
              <p className="text-sm text-gray-600 mb-2">Assigned Seats</p>
              <p className="text-3xl font-bold text-green-600">{assignedSeats}</p>
            </div>

            {/* Vacant Seats (Auto) */}
            <div className="p-4 bg-yellow-50 border-l-4 border-yellow-500 rounded-lg">
              <p className="text-sm text-gray-600 mb-2">Available Seats</p>
              <p className="text-3xl font-bold text-yellow-600">
                {vacantSeats >= 0 ? vacantSeats : 0}
              </p>
            </div>

          </div>
        </div>

        {/* Subscription History (Read-only) */}
        <div>
          <h2 className="text-xl font-semibold text-gray-700 mb-3">Subscription Details</h2>

          {user.membershipsDetails?.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="table">
                <thead>
                  <tr className="table-heading-bg">
                    <th className="table-heading">Start Date</th>
                    <th className="table-heading">End Date</th>
                    <th className="table-heading">Cost</th>
                    <th className="table-heading">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {user.membershipsDetails.map((m, index) => (
                    <tr
                      key={index}
                      className="border-b hover:bg-gray-50 transition"
                    >
                      <td className="table-body-td">
                        {new Date(m.startDate).toLocaleDateString("en-IN")}
                      </td>
                      <td className="table-body-td">
                        {new Date(m.endDate).toLocaleDateString("en-IN")}
                      </td>
                      <td className="table-body-td">₹{m.cost}</td>
                      <td className="table-body-td">
                        <span
                          className={`px-2 py-1 rounded text-xs font-semibold ${
                            m.paymentStatus === "Paid"
                              ? "bg-green-100 text-green-700"
                              : m.paymentStatus === "Pending"
                              ? "bg-yellow-100 text-yellow-700"
                              : "bg-red-100 text-red-700"
                          }`}
                        >
                          {m.paymentStatus}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-gray-600 italic">No subscription history found.</p>
          )}
        </div>

        {/* Save Button */}
        <div className="flex justify-end pt-4">
          <button
            onClick={handleSave}
            className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition font-medium shadow-md hover:shadow-lg"
          >
            Save Changes
          </button>
        </div>

      </div>
    </div>
  );
}